import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Download, Share2, Search, Grid, List, Video, Music, Image, FileText, X, Plus, Activity, File, FileText as FileTextIcon } from 'lucide-react';

interface MediaItem {
  id: string;
  type: 'video' | 'audio' | 'image' | 'document' | 'interactive';
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
  duration?: number; // in seconds
  size?: number; // in bytes
  format: string;
  quality?: 'low' | 'medium' | 'high' | 'ultra';
  isEmbedded: boolean;
  isDownloadable: boolean;
  isShareable: boolean;
  createdAt: Date;
  updatedAt: Date;
  author: string;
  tags: string[];
  metadata: {
    width?: number;
    height?: number;
    bitrate?: number;
    framerate?: number;
    codec?: string;
    language?: string;
    subtitles?: boolean;
    captions?: boolean;
  };
}

interface MediaIntegrationProps {
  articleId: string;
  onMediaAdd?: (media: MediaItem) => void;
  onMediaUpdate?: (media: MediaItem) => void;
  onMediaDelete?: (mediaId: string) => void;
  onMediaPlay?: (media: MediaItem) => void;
  onMediaPause?: (media: MediaItem) => void;
  onMediaDownload?: (media: MediaItem) => void;
  onMediaShare?: (media: MediaItem) => void;
}

const MediaIntegration: React.FC<MediaIntegrationProps> = ({
  articleId,
  onMediaAdd,
  onMediaUpdate,
  onMediaDelete,
  onMediaPlay,
  onMediaPause,
  onMediaDownload,
  onMediaShare
}) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement | null>(null);
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMediaItems();
  }, [articleId]);

  useEffect(() => {
    if (selectedMedia && mediaRef.current) {
      mediaRef.current.addEventListener('timeupdate', handleTimeUpdate);
      mediaRef.current.addEventListener('ended', handleEnded);
      mediaRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      return () => {
        if (mediaRef.current) {
          mediaRef.current.removeEventListener('timeupdate', handleTimeUpdate);
          mediaRef.current.removeEventListener('ended', handleEnded);
          mediaRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
        }
      };
    }
  }, [selectedMedia]);

  const loadMediaItems = async () => {
    try {
      setIsLoading(true);
      // In a real app, this would fetch from your backend
      const sampleMedia: MediaItem[] = [
        {
          id: '1',
          type: 'video',
          title: 'Product Demo Video',
          description: 'A comprehensive demonstration of our new features',
          url: 'https://sample-videos.com/zip/10/mp4/SampleVideo_1280x720_1mb.mp4',
          thumbnail: 'https://via.placeholder.com/320x180',
          duration: 120,
          size: 1048576,
          format: 'mp4',
          quality: 'high',
          isEmbedded: true,
          isDownloadable: true,
          isShareable: true,
          createdAt: new Date('2024-01-01'),
          updatedAt: new Date('2024-01-15'),
          author: 'current-user',
          tags: ['demo', 'product', 'features'],
          metadata: {
            width: 1280,
            height: 720,
            bitrate: 1000,
            framerate: 30,
            codec: 'h264',
            language: 'en',
            subtitles: true,
            captions: true
          }
        },
        {
          id: '2',
          type: 'audio',
          title: 'Podcast Episode 1',
          description: 'Weekly discussion about industry trends',
          url: 'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav',
          thumbnail: 'https://via.placeholder.com/300x300',
          duration: 1800,
          size: 2097152,
          format: 'mp3',
          quality: 'high',
          isEmbedded: true,
          isDownloadable: true,
          isShareable: true,
          createdAt: new Date('2024-01-05'),
          updatedAt: new Date('2024-01-10'),
          author: 'current-user',
          tags: ['podcast', 'discussion', 'trends'],
          metadata: {
            bitrate: 128,
            codec: 'mp3',
            language: 'en',
            subtitles: false,
            captions: true
          }
        },
        {
          id: '3',
          type: 'image',
          title: 'Infographic: Data Trends',
          description: 'Visual representation of key data insights',
          url: 'https://via.placeholder.com/800x600',
          thumbnail: 'https://via.placeholder.com/320x240',
          size: 524288,
          format: 'png',
          quality: 'high',
          isEmbedded: true,
          isDownloadable: true,
          isShareable: true,
          createdAt: new Date('2024-01-10'),
          updatedAt: new Date('2024-01-20'),
          author: 'current-user',
          tags: ['infographic', 'data', 'insights'],
          metadata: {
            width: 800,
            height: 600
          }
        },
        {
          id: '4',
          type: 'document',
          title: 'Technical Specification',
          description: 'Detailed technical documentation',
          url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
          thumbnail: 'https://via.placeholder.com/200x280',
          size: 1048576,
          format: 'pdf',
          isEmbedded: true,
          isDownloadable: true,
          isShareable: true,
          createdAt: new Date('2024-01-15'),
          updatedAt: new Date('2024-01-25'),
          author: 'current-user',
          tags: ['documentation', 'technical', 'specs'],
          metadata: {
            language: 'en'
          }
        }
      ];
      setMediaItems(sampleMedia);
    } catch (err) {
      setError('Failed to load media items');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimeUpdate = () => {
    if (mediaRef.current) {
      setCurrentTime(mediaRef.current.currentTime);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleLoadedMetadata = () => {
    if (mediaRef.current) {
      setCurrentTime(0);
    }
  };

  const handlePlay = () => {
    if (mediaRef.current) {
      if (isPlaying) {
        mediaRef.current.pause();
        setIsPlaying(false);
        onMediaPause?.(selectedMedia!);
      } else {
        mediaRef.current.play();
        setIsPlaying(true);
        onMediaPlay?.(selectedMedia!);
      }
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mediaRef.current && progressRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * (selectedMedia?.duration || 0);
      
      mediaRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    if (mediaRef.current) {
      mediaRef.current.volume = newVolume;
    }
  };

  const handleMute = () => {
    setIsMuted(!isMuted);
    if (mediaRef.current) {
      mediaRef.current.muted = !isMuted;
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackSpeed(speed);
    if (mediaRef.current) {
      mediaRef.current.playbackRate = speed;
    }
  };

  const handleSkipBack = () => {
    if (mediaRef.current) {
      mediaRef.current.currentTime = Math.max(0, currentTime - 10);
    }
  };

  const handleSkipForward = () => {
    if (mediaRef.current && selectedMedia) {
      mediaRef.current.currentTime = Math.min(selectedMedia.duration || 0, currentTime + 10);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getMediaIcon = (type: string) => {
    switch (type) {
      case 'video': return Video;
      case 'audio': return Music;
      case 'image': return Image;
      case 'document': return FileText;
      case 'interactive': return Activity;
      default: return File;
    }
  };

  const getMediaColor = (type: string) => {
    switch (type) {
      case 'video': return 'text-red-600 dark:text-red-400';
      case 'audio': return 'text-blue-600 dark:text-blue-400';
      case 'image': return 'text-green-600 dark:text-green-400';
      case 'document': return 'text-purple-600 dark:text-purple-400';
      case 'interactive': return 'text-orange-600 dark:text-orange-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getMediaBgColor = (type: string) => {
    switch (type) {
      case 'video': return 'bg-red-100 dark:bg-red-900';
      case 'audio': return 'bg-blue-100 dark:bg-blue-900';
      case 'image': return 'bg-green-100 dark:bg-green-900';
      case 'document': return 'bg-purple-100 dark:bg-purple-900';
      case 'interactive': return 'bg-orange-100 dark:bg-orange-900';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  const filteredMedia = mediaItems.filter(item => {
    const matchesType = filterType === 'all' || item.type === filterType;
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return matchesType && matchesSearch;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Media Integration
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Embed and interact with videos, audio, images, and documents
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search media..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="video">Videos</option>
              <option value="audio">Audio</option>
              <option value="image">Images</option>
              <option value="document">Documents</option>
              <option value="interactive">Interactive</option>
            </select>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>

            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Media
            </button>
          </div>
        </div>
      </div>

      {/* Media Player */}
      {selectedMedia && (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {selectedMedia.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {selectedMedia.description}
            </p>
          </div>

          {/* Media Element */}
          <div className="mb-4">
            {selectedMedia.type === 'video' ? (
              <video
                ref={mediaRef as React.RefObject<HTMLVideoElement>}
                src={selectedMedia.url}
                poster={selectedMedia.thumbnail}
                className="w-full h-64 bg-black rounded-lg"
                controls={false}
              />
            ) : selectedMedia.type === 'audio' ? (
              <div className="flex items-center justify-center h-32 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <audio
                  ref={mediaRef as React.RefObject<HTMLAudioElement>}
                  src={selectedMedia.url}
                  className="w-full"
                />
                <div className="text-center">
                  <div className={`p-4 rounded-full ${getMediaBgColor(selectedMedia.type)} mb-2`}>
                    {React.createElement(getMediaIcon(selectedMedia.type), {
                      className: `h-8 w-8 ${getMediaColor(selectedMedia.type)}`
                    })}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedMedia.title}
                  </p>
                </div>
              </div>
            ) : selectedMedia.type === 'image' ? (
              <img
                src={selectedMedia.url}
                alt={selectedMedia.title}
                className="w-full h-64 object-cover rounded-lg"
              />
            ) : (
              <div className="flex items-center justify-center h-32 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <div className="text-center">
                  <div className={`p-4 rounded-full ${getMediaBgColor(selectedMedia.type)} mb-2`}>
                    {React.createElement(getMediaIcon(selectedMedia.type), {
                      className: `h-8 w-8 ${getMediaColor(selectedMedia.type)}`
                    })}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedMedia.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    {selectedMedia.format.toUpperCase()} • {formatFileSize(selectedMedia.size || 0)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {(selectedMedia.type === 'video' || selectedMedia.type === 'audio') && (
            <div className="mb-4">
              <div
                ref={progressRef}
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer"
                onClick={handleSeek}
              >
                <div
                  className="h-2 bg-blue-500 rounded-full transition-all duration-200"
                  style={{
                    width: `${selectedMedia.duration ? (currentTime / selectedMedia.duration) * 100 : 0}%`
                  }}
                />
              </div>
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(selectedMedia.duration || 0)}</span>
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleSkipBack}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <SkipBack className="h-5 w-5" />
              </button>
              
              <button
                onClick={handlePlay}
                className="p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600"
              >
                {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
              </button>
              
              <button
                onClick={handleSkipForward}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <SkipForward className="h-5 w-5" />
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleMute}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                  className="w-20"
                />
              </div>

              <select
                value={playbackSpeed}
                onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value={0.5}>0.5x</option>
                <option value={0.75}>0.75x</option>
                <option value={1}>1x</option>
                <option value={1.25}>1.25x</option>
                <option value={1.5}>1.5x</option>
                <option value={2}>2x</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => onMediaDownload?.(selectedMedia)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title="Download"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                onClick={() => onMediaShare?.(selectedMedia)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title="Share"
              >
                <Share2 className="h-5 w-5" />
              </button>
              <button
                onClick={() => setSelectedMedia(null)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                title="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Media Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMedia.map((item) => {
            const MediaIcon = getMediaIcon(item.type);
            return (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedMedia(item)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-lg ${getMediaBgColor(item.type)}`}>
                    <MediaIcon className={`h-6 w-6 ${getMediaColor(item.type)}`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMediaBgColor(item.type)} ${getMediaColor(item.type)}`}>
                      {item.type}
                    </span>
                    {item.quality && (
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        {item.quality}
                      </span>
                    )}
                  </div>
                </div>

                {item.thumbnail && (
                  <div className="mb-4">
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                  </div>
                )}

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {item.description}
                </p>

                <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                  <div>
                    {item.duration && (
                      <span>{formatTime(item.duration)}</span>
                    )}
                    {item.size && (
                      <span className="ml-2">{formatFileSize(item.size)}</span>
                    )}
                  </div>
                  <div>
                    {item.format.toUpperCase()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredMedia.map((item) => {
            const MediaIcon = getMediaIcon(item.type);
            return (
              <div
                key={item.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedMedia(item)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${getMediaBgColor(item.type)}`}>
                      <MediaIcon className={`h-6 w-6 ${getMediaColor(item.type)}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {item.description}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getMediaBgColor(item.type)} ${getMediaColor(item.type)}`}>
                        {item.type}
                      </span>
                      {item.quality && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          {item.quality}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {item.duration && formatTime(item.duration)}
                      {item.size && ` • ${formatFileSize(item.size)}`}
                      <span className="ml-2">{item.format.toUpperCase()}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredMedia.length === 0 && (
        <div className="text-center py-12">
          <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No media found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Add videos, audio, images, or documents to enhance your content
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Add Media
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaIntegration;
